import { useState, useMemo } from 'react';
import { X, Trash2, Calendar, ArrowRight, Loader2, ListFilter, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';

export default function PersonalChangesModal({
    isOpen,
    onClose,
    changes,
    turnos,
    onDelete,
    onSelectDate
}) {
    const [activeFilter, setActiveFilter] = useState('todas');
    const [expandedGroups, setExpandedGroups] = useState({});

    // Toggle para los acordeones
    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    // Build categories from changes data
    const categories = useMemo(() => {
        const cats = [{ id: 'todas', label: 'Todas', color: '#06b6d4', count: changes.length }];
        const grouped = {};

        changes.forEach(change => {
            let catId, catLabel, catColor;

            if (change.motivo_nombre) {
                catId = `motivo_${change.motivo_id}`;
                catLabel = change.motivo_nombre;
                catColor = change.motivo_color || '#06b6d4';
            } else if (change.turno_id) {
                catId = 'turno';
                catLabel = 'Cambio Turno';
                catColor = '#8b5cf6';
            } else {
                catId = 'nota';
                catLabel = 'Nota Personal';
                catColor = '#3b82f6';
            }

            if (!grouped[catId]) {
                grouped[catId] = { id: catId, label: catLabel, color: catColor, count: 0 };
            }
            grouped[catId].count++;

            // NUEVO: Si tiene comentario y ya está en otra categoría, también contarlo en "Nota Personal"
            if (change.comentario?.trim() && catId !== 'nota') {
                if (!grouped['nota']) {
                    grouped['nota'] = { id: 'nota', label: 'Nota Personal', color: '#3b82f6', count: 0 };
                }
                grouped['nota'].count++;
            }
        });

        return [...cats, ...Object.values(grouped)];
    }, [changes]);

    // Filter changes based on active filter
    const filteredChanges = useMemo(() => {
        if (activeFilter === 'todas') return changes;

        return changes.filter(change => {
            if (activeFilter.startsWith('motivo_')) {
                return `motivo_${change.motivo_id}` === activeFilter;
            }
            if (activeFilter === 'turno') {
                return change.turno_id && !change.motivo_nombre;
            }
            if (activeFilter === 'nota') {
                // MODIFICADO: Mostrar en "Nota Personal" si tiene comentario (aunque tenga motivo)
                return change.comentario?.trim();
            }
            return true;
        });
    }, [changes, activeFilter]);

    // Sort changes by date (newest first for the list)
    const sortedChanges = useMemo(() => {
        return [...filteredChanges].sort((a, b) => {
            const dateA = new Date(a.fecha.split('T')[0]);
            const dateB = new Date(b.fecha.split('T')[0]);
            return dateB - dateA;
        });
    }, [filteredChanges]);

    // Agrupar por meses
    const groupedChanges = useMemo(() => {
        const groups = {};
        const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        
        sortedChanges.forEach(change => {
            const datePart = change.fecha.split('T')[0];
            const [year, month, day] = datePart.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            const groupKey = `${monthNames[date.getMonth()]} ${year}`;
            
            if (!groups[groupKey]) {
                groups[groupKey] = {
                    key: groupKey,
                    month: date.getMonth(),
                    year,
                    items: []
                };
            }
            groups[groupKey].items.push(change);
        });
        
        return Object.values(groups);
    }, [sortedChanges]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/70">
            <div className="relative bg-gradient-to-br from-[#1a1a3e] to-[#0f0f2a] rounded-2xl shadow-2xl border border-white/10 w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-5 border-b border-white/10 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                            <ListFilter className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Mis Modificaciones</h2>
                            <p className="text-xs text-gray-400">Resumen del historial de cambios personales</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Filter Chips */}
                {changes.length > 0 && (
                    <div className="px-4 py-3 border-b border-white/10 shrink-0 overflow-x-auto">
                        <div className="flex gap-2 min-w-max">
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => setActiveFilter(cat.id)}
                                    className={`
                                        flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide
                                        transition-all duration-200 min-h-[44px] whitespace-nowrap
                                        ${activeFilter === cat.id
                                            ? 'text-white shadow-lg scale-105'
                                            : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                                        }
                                    `}
                                    style={activeFilter === cat.id ? {
                                        backgroundColor: cat.color,
                                        boxShadow: `0 4px 20px ${cat.color}40`
                                    } : {}}
                                >
                                    <span>{cat.label}</span>
                                    <span className={`
                                        px-1.5 py-0.5 rounded-full text-[10px] font-black
                                        ${activeFilter === cat.id ? 'bg-white/20' : 'bg-white/10'}
                                    `}>
                                        {cat.count}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Content */}
                <div className="p-4 overflow-y-auto flex-1 custom-scrollbar">
                    {sortedChanges.length === 0 && activeFilter !== 'todas' ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                                <ListFilter className="w-8 h-8 text-gray-500" />
                            </div>
                            <h3 className="text-white font-medium">Sin resultados</h3>
                            <p className="text-gray-500 text-sm mt-1 max-w-xs">
                                No hay modificaciones en esta categoría.
                            </p>
                            <button
                                onClick={() => setActiveFilter('todas')}
                                className="mt-4 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-xl text-xs font-bold uppercase tracking-wide hover:bg-cyan-500/30 transition-colors"
                            >
                                Ver todas
                            </button>
                        </div>
                    ) : sortedChanges.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                                <AlertCircle className="w-8 h-8 text-gray-500" />
                            </div>
                            <h3 className="text-white font-medium">No hay modificaciones</h3>
                            <p className="text-gray-500 text-sm mt-1 max-w-xs">
                                Todos tus días siguen el cuadrante original según tu grupo y turno.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {groupedChanges.map((group, groupIdx) => {
                                // El primer grupo por defecto está expandido si no hay estado previo
                                const isExpanded = expandedGroups[group.key] !== undefined 
                                    ? expandedGroups[group.key] 
                                    : groupIdx === 0;

                                return (
                                    <div key={group.key} className="space-y-2">
                                        {/* Header de Mes (Acordeón) */}
                                        <button 
                                            onClick={() => toggleGroup(group.key)}
                                            className="w-full flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all group/month"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400">
                                                    <Calendar className="w-4 h-4" />
                                                </div>
                                                <span className="text-sm font-bold text-gray-200 group-hover/month:text-white transition-colors">
                                                    {group.key}
                                                </span>
                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-gray-500 font-bold border border-white/5">
                                                    {group.items.length} {group.items.length === 1 ? 'cambio' : 'cambios'}
                                                </span>
                                            </div>
                                            {isExpanded ? (
                                                <ChevronDown className="w-4 h-4 text-gray-500 group-hover/month:text-cyan-400" />
                                            ) : (
                                                <ChevronRight className="w-4 h-4 text-gray-500 group-hover/month:text-cyan-400" />
                                            )}
                                        </button>

                                        {/* Lista de cambios en el mes */}
                                        {isExpanded && (
                                            <div className="space-y-2 pl-2">
                                                {group.items.map((change) => {
                                                    const datePart = change.fecha.split('T')[0];
                                                    const [year, month, day] = datePart.split('-').map(Number);
                                                    const date = new Date(year, month - 1, day);

                                                    const dayNum = date.getDate();
                                                    const monthShort = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][date.getMonth()];
                                                    const turnoMod = turnos.find(t => t.id === change.turno_id);

                                                    return (
                                                        <div
                                                            key={change.id}
                                                            className="group relative bg-white/[0.03] border border-white/5 hover:border-cyan-500/30 rounded-2xl p-3 transition-all hover:bg-white/[0.06] cursor-pointer"
                                                            onClick={() => onSelectDate(date)}
                                                        >
                                                            <div className="flex items-center gap-4">
                                                                {/* Date Box */}
                                                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/10 flex flex-col items-center justify-center shrink-0">
                                                                    <span className="text-base font-bold text-cyan-400 leading-none">{dayNum}</span>
                                                                    <span className="text-[8px] uppercase font-bold text-cyan-500/70 mt-0.5">{monthShort}</span>
                                                                </div>

                                                                {/* Details */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        {activeFilter === 'nota' ? (
                                                                            <span className="text-white font-semibold text-xs flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-500/20 text-blue-400">
                                                                                📝 Nota Personal
                                                                            </span>
                                                                        ) : change.motivo_nombre ? (
                                                                            <span
                                                                                className="text-white font-semibold text-xs px-2 py-0.5 rounded-md"
                                                                                style={{ backgroundColor: `${change.motivo_color}40`, color: change.motivo_color }}
                                                                            >
                                                                                {change.motivo_nombre}
                                                                            </span>
                                                                        ) : change.turno_id ? (
                                                                            <span className="text-white font-semibold text-xs">Cambio de Turno</span>
                                                                        ) : (
                                                                            <span className="text-white font-semibold text-xs flex items-center gap-1">
                                                                                📝 Nota Personal
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    {turnoMod && (
                                                                        <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                                                            <span>Turno:</span>
                                                                            <span className="text-gray-200 font-medium">{turnoMod.nombre}</span>
                                                                        </div>
                                                                    )}

                                                                    {change.comentario && (
                                                                        <p className="text-[10px] text-gray-500 mt-1 italic line-clamp-1">
                                                                            "{change.comentario}"
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                {/* Delete Button */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onDelete(change.id);
                                                                    }}
                                                                    className="p-2 rounded-lg bg-red-500/5 text-red-500 lg:opacity-0 lg:group-hover:opacity-100 hover:bg-red-500/20 transition-all shrink-0"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-white/10 bg-black/20 text-center shrink-0">
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest font-medium">
                        Pulsa en un cambio para ver el día en el calendario
                    </p>
                </div>
            </div>
        </div>
    );
}
